// Resolve a card image URL for the current runtime.
//
// Card images live in web/public/generated/cards and are committed by the daily
// cron, so Cloudflare Pages serves them for free (with Access-Control-Allow-Origin:*
// already set). The web app (prod or local dev) is served over http(s) from a domain
// that hosts those paths, so a bundle-relative "/generated/cards/x.jpg" already
// resolves there. The NATIVE app, however, runs from capacitor://localhost (or
// file://) and only bundles a snapshot, so relative paths to images generated after
// the build would 404. In that case we rewrite the relative path to an absolute URL
// on the production asset domain (which has the full, always-fresh set).
const ASSET_BASE = (import.meta.env.VITE_ASSET_BASE || "https://insnip.com").replace(/\/+$/, "");

// A stable hue (0-359) per card, so the image fallback reads as a designed,
// topic-tinted cover rather than an empty panel. Known categories get a curated
// hue; anything else is hashed deterministically so the same card is always the
// same colour.
const CATEGORY_HUES: Record<string, number> = {
  // The 7 anchor categories (full names, as stored on cards).
  "money & work": 42,
  "science & systems": 200,
  "technology & tools": 222,
  "culture & curiosity": 320,
  "power & policy": 258,
  "sport & play": 18,
  "planet & environment": 130,
  // Short aliases / tags as a fallback.
  business: 42,
  science: 200,
  health: 150,
  tech: 222,
  technology: 222,
  sport: 18,
  sports: 18,
  environment: 130,
  climate: 130,
  culture: 320,
  world: 258,
  politics: 350,
};

export function imageTintHue(card: { category?: string; headline?: string } | undefined | null): number {
  const cat = (card?.category || "").toLowerCase().trim();
  if (cat in CATEGORY_HUES) return CATEGORY_HUES[cat];
  const key = cat || card?.headline || "insnip";
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) % 360;
  return h;
}

// A tiny, blurred placeholder derived from an ImageKit URL (≈300 bytes), shown
// instantly behind the full image so a card never flashes empty. Returns "" for
// non-ImageKit URLs (the topic-tinted panel covers those).
export function imageLqip(url: string | undefined | null): string {
  if (!url || !url.includes("ik.imagekit.io") || !url.includes("/tr:")) return "";
  return url.replace(/\/tr:[^/]+\//, "/tr:w-40,bl-10,q-30,f-auto/");
}

export function resolveImageUrl(url: string | undefined | null): string {
  if (!url) return "";
  if (/^(https?:|data:|blob:)/i.test(url)) return url; // already absolute
  const rel = url.startsWith("/") ? url : `/${url}`;
  // On a real web origin the relative path is already served; only rewrite to the
  // absolute asset domain when the page is not served over http(s) (native webview).
  const origin = typeof location !== "undefined" ? location.origin : "";
  if (/^https?:/i.test(origin)) return rel;
  return ASSET_BASE + rel;
}
